import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface InlineAutocompleteSuggestionProps {
  suggestion: string;
  isLoading: boolean;
  visible: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  cursorPosition: number;
}

export const InlineAutocompleteSuggestion: React.FC<InlineAutocompleteSuggestionProps> = ({
  suggestion,
  isLoading,
  visible,
  textareaRef,
  cursorPosition
}) => {
  const { t } = useTranslation();
  const overlayRef = useRef<HTMLDivElement>(null);


  // Create overlay with exact textarea styling
  useEffect(() => {
    if (!textareaRef.current || !overlayRef.current || !visible) return;

    const textarea = textareaRef.current;
    const overlay = overlayRef.current;
    
    // Copy all relevant styles from textarea to overlay
    const computedStyle = window.getComputedStyle(textarea);
    const stylesToCopy = [
      'font-family', 'font-size', 'font-weight', 'font-style',
      'line-height', 'letter-spacing', 'word-spacing',
      'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
      'border-top-width', 'border-right-width', 'border-bottom-width', 'border-left-width',
      'white-space', 'word-wrap', 'overflow-wrap'
    ];
    
    stylesToCopy.forEach(prop => {
      overlay.style.setProperty(prop, computedStyle.getPropertyValue(prop));
    });
    
    // Set fixed positioning
    overlay.style.position = 'absolute';
    overlay.style.top = `${textarea.offsetTop}px`;
    overlay.style.left = `${textarea.offsetLeft}px`;
    overlay.style.width = `${textarea.offsetWidth}px`;
    overlay.style.height = `${textarea.offsetHeight}px`;
    overlay.style.pointerEvents = 'none';
    overlay.style.zIndex = '10';
    overlay.style.overflow = 'hidden';
    
    // Create the suggestion text
    const textBeforeCursor = textarea.value.substring(0, cursorPosition);
    const textAfterCursor = textarea.value.substring(cursorPosition);
    
    overlay.innerHTML = '';
    
    // Invisible text before cursor
    const beforeSpan = document.createElement('span');
    beforeSpan.textContent = textBeforeCursor;
    beforeSpan.style.visibility = 'hidden';
    overlay.appendChild(beforeSpan);
    
    // Visible suggestion
    if (suggestion) {
      const suggestionSpan = document.createElement('span');
      suggestionSpan.textContent = suggestion;
      suggestionSpan.style.color = '#9CA3AF';
      suggestionSpan.style.backgroundColor = 'rgba(156, 163, 175, 0.1)';
      overlay.appendChild(suggestionSpan);
    }
    
    // Invisible text after cursor
    const afterSpan = document.createElement('span');
    afterSpan.textContent = textAfterCursor;
    afterSpan.style.visibility = 'hidden';
    overlay.appendChild(afterSpan);
    
  }, [textareaRef, visible, suggestion, cursorPosition]);

  if (!visible && !isLoading) return null;

  return (
    <div className="relative">
      {/* Textarea overlay for inline suggestions */}
      {visible && suggestion && (
        <div
          ref={overlayRef}
          className="absolute inset-0 pointer-events-none"
          style={{ zIndex: 10 }}
        />
      )}
      
      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute z-20 bg-blue-50 border border-blue-200 rounded-md px-2 py-1 text-xs text-blue-600 shadow-sm animate-pulse"
             style={{
               top: '100%',
               left: '0',
               marginTop: '4px'
             }}>
          <div className="flex items-center space-x-1">
            <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            <span className="ml-1">
              {t('autocomplete.generating', { defaultValue: 'Analisando contexto...' })}
            </span>
          </div>
        </div>
      )}
      
      {/* Hint for Ctrl+B completion */}
      {visible && suggestion && !isLoading && (
        <div className="absolute z-20 bg-ghost-800/90 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-md pointer-events-none opacity-80"
             style={{
               top: '100%',
               right: '0',
               marginTop: '4px'
             }}>
          <div className="flex items-center space-x-1">
            <kbd className="bg-white/20 px-1 py-0.5 rounded text-xs font-mono">Ctrl+B</kbd>
            <span>{t('autocomplete.pressCtrlB', { defaultValue: 'aceitar' })}</span>
            <span className="mx-1">•</span>
            <kbd className="bg-white/20 px-1 py-0.5 rounded text-xs font-mono">Esc</kbd>
            <span>dispensar</span>
          </div>
        </div>
      )}
    </div>
  );
};